/**
 * Envelope encryption for SportsFuture applications.
 * AES-256-GCM payload + X25519-wrapped DEKs for reviewers.
 * Ciphertext is pinned to IPFS (Pinata). The chain only gets contentHash + ipfs://cid.
 */

import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { utils as ethUtils } from 'ethers';
import { REVIEWER_PUBLIC_KEYS } from './reviewerKeys';

export const APPLICANT_CRYPTO_V = 1;
export const WRAP_INFO = new TextEncoder().encode('sf-app-dek-wrap-v1');

export interface EncryptedBlob {
  iv: string;
  ct: string;
  tag: string;
}

export interface WrappedDek {
  kid: string;
  alg: 'X25519-HKDF-AES-256-GCM';
  ephPub: string;
  wrap: EncryptedBlob;
}

export interface SealedApplication {
  v: typeof APPLICANT_CRYPTO_V;
  alg: 'AES-256-GCM';
  didId: number;
  role: string;
  plaintextHash: string;
  payload: EncryptedBlob;
  files: Array<{ name: string; type: string; wrap: EncryptedBlob }>;
  wrappedKeys: WrappedDek[];
}

export interface ReviewerKeypair {
  kid: string;
  secretHex: string;
  publicHex: string;
}

export function bytesToHex(bytes: Uint8Array): string {
  return `0x${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export function hexToBytes(hex: string): Uint8Array {
  const h = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (h.length % 2 !== 0) throw new Error('invalid hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToB64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64');
  let s = '';
  bytes.forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s);
}

export function b64ToBytes(b64: string): Uint8Array {
  if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(b64, 'base64'));
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) out[key] = sortValue(obj[key]);
    return out;
  }
  return value;
}

export function contentHashOf(plaintext: unknown): string {
  return ethUtils.keccak256(ethUtils.toUtf8Bytes(canonicalJson(plaintext)));
}

export function generateReviewerKeypair(kid = 'sf-admin-1'): ReviewerKeypair {
  const secret = x25519.utils.randomSecretKey();
  const pub = x25519.getPublicKey(secret);
  return { kid, secretHex: bytesToHex(secret), publicHex: bytesToHex(pub) };
}

export function parseReviewerPublicKeys(
  raw: string | undefined
): Array<{ kid: string; publicHex: string }> {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((part, i) => part.trim())
    .filter(Boolean)
    .map((hex, i) => ({
      kid: `sf-admin-${i + 1}`,
      publicHex: hex.startsWith('0x') ? hex : `0x${hex}`,
    }))
    .filter((k) => hexToBytes(k.publicHex).length === 32);
}

async function aesGcmEncrypt(
  keyBytes: Uint8Array,
  plaintext: Uint8Array
): Promise<EncryptedBlob> {
  const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buf = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext));
  const tag = buf.slice(buf.length - 16);
  const ct = buf.slice(0, buf.length - 16);
  return { iv: bytesToB64(iv), ct: bytesToB64(ct), tag: bytesToB64(tag) };
}

async function aesGcmDecrypt(keyBytes: Uint8Array, blob: EncryptedBlob): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['decrypt']);
  const iv = b64ToBytes(blob.iv);
  const ct = b64ToBytes(blob.ct);
  const tag = b64ToBytes(blob.tag);
  const packed = new Uint8Array(ct.length + tag.length);
  packed.set(ct, 0);
  packed.set(tag, ct.length);
  const buf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, packed);
  return new Uint8Array(buf);
}

async function wrapDek(
  dek: Uint8Array,
  recipientPubHex: string,
  kid: string
): Promise<WrappedDek> {
  const ephSecret = x25519.utils.randomSecretKey();
  const ephPub = x25519.getPublicKey(ephSecret);
  const shared = x25519.getSharedSecret(ephSecret, hexToBytes(recipientPubHex));
  const wrapKey = hkdf(sha256, shared, undefined, WRAP_INFO, 32);
  return {
    kid,
    alg: 'X25519-HKDF-AES-256-GCM',
    ephPub: bytesToHex(ephPub),
    wrap: await aesGcmEncrypt(wrapKey, dek),
  };
}

async function unwrapDek(wrapped: WrappedDek, secretHex: string): Promise<Uint8Array> {
  const shared = x25519.getSharedSecret(hexToBytes(secretHex), hexToBytes(wrapped.ephPub));
  const wrapKey = hkdf(sha256, shared, undefined, WRAP_INFO, 32);
  return aesGcmDecrypt(wrapKey, wrapped.wrap);
}

export async function sealApplicantRecord(opts: {
  didId: number;
  role: string;
  plaintext: Record<string, unknown>;
  files?: Array<{ name: string; type: string; bytes: Uint8Array }>;
  reviewerPubs: Array<{ kid: string; publicHex: string }>;
}): Promise<SealedApplication> {
  if (!opts.reviewerPubs.length) {
    throw new Error('No reviewer encryption keys configured (VITE_ADMIN_ENC_PUBKEYS).');
  }
  const dek = crypto.getRandomValues(new Uint8Array(32));
  const payloadBytes = new TextEncoder().encode(canonicalJson(opts.plaintext));
  const payload = await aesGcmEncrypt(dek, payloadBytes);
  const files: SealedApplication['files'] = [];
  for (const file of opts.files ?? []) {
    files.push({
      name: file.name,
      type: file.type,
      wrap: await aesGcmEncrypt(dek, file.bytes),
    });
  }
  const wrappedKeys = [];
  for (const rec of opts.reviewerPubs) {
    wrappedKeys.push(await wrapDek(dek, rec.publicHex, rec.kid));
  }
  return {
    v: APPLICANT_CRYPTO_V,
    alg: 'AES-256-GCM',
    didId: opts.didId,
    role: opts.role,
    plaintextHash: contentHashOf(opts.plaintext),
    payload,
    files,
    wrappedKeys,
  };
}

export async function openApplicantRecord(
  sealed: SealedApplication,
  reviewerSecretHex: string
): Promise<{
  plaintext: Record<string, unknown>;
  files: Array<{ name: string; type: string; bytes: Uint8Array }>;
}> {
  let lastErr: unknown;
  for (const wrapped of sealed.wrappedKeys) {
    try {
      const dek = await unwrapDek(wrapped, reviewerSecretHex);
      const raw = await aesGcmDecrypt(dek, sealed.payload);
      const plaintext = JSON.parse(new TextDecoder().decode(raw)) as Record<string, unknown>;
      if (contentHashOf(plaintext) !== sealed.plaintextHash) {
        throw new Error('plaintext hash mismatch');
      }
      const files = [];
      for (const file of sealed.files) {
        files.push({
          name: file.name,
          type: file.type,
          bytes: await aesGcmDecrypt(dek, file.wrap),
        });
      }
      return { plaintext, files };
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Unable to unwrap application DEK');
}

const ALLOWED_SEAL_KEYS = new Set([
  'v',
  'alg',
  'didId',
  'role',
  'plaintextHash',
  'payload',
  'files',
  'wrappedKeys',
]);

export function sealedLooksCiphertextOnly(sealed: SealedApplication): boolean {
  return Object.keys(sealed).every((k) => ALLOWED_SEAL_KEYS.has(k));
}

/** Public notice only — never put name/email/phone in Telegram. */
export function applicationSubmittedNotice(opts: {
  didId: number;
  role: string;
}): string {
  return `New SportsFuture ${opts.role} application (DID #${opts.didId}). Review in admin.`;
}

export function configuredReviewerPubs(): Array<{ kid: string; publicHex: string }> {
  const raw =
    typeof import.meta !== 'undefined'
      ? import.meta.env.VITE_ADMIN_ENC_PUBKEYS
      : undefined;
  const fromEnv = parseReviewerPublicKeys(raw);
  const seen = new Set(fromEnv.map((k) => k.publicHex.toLowerCase()));
  const merged = [...fromEnv];
  for (const k of REVIEWER_PUBLIC_KEYS) {
    if (!seen.has(k.publicHex.toLowerCase())) {
      merged.push({ kid: k.kid, publicHex: k.publicHex });
    }
  }
  return merged;
}
